import { OverlayRect } from "./search_field"

// A multi-line DOM <textarea> parked over a box a pixi panel draws: SearchField's idea, for prose.
//
// Same reasons as SearchField (a phone raises its keyboard only for a real field, and a tap on the
// element itself is the user gesture it wants), plus one of its own: a description is sentences, so
// it needs wrapping, autocorrect and sentence case, which a single-line search box turns off.
//
// The panel owns WHERE the field is (show/hide with a page rectangle); the field owns the keyboard
// while it has focus. Enter sends, Shift+Enter breaks a line, Escape hands the keyboard back.

export interface PromptFieldProps {
    placeholder: string
    ariaLabel: string
    maxLength: number
    onInput: (value: string) => void
    // Enter without Shift. The field keeps its text; the owner clears it once the request lands.
    onSubmit: () => void
    // Focus arrived or left. Fired AFTER the keyboard has been taken from (or given back to) the game.
    onFocusChange?: (focused: boolean) => void
    // A press landed on the field. The owner cancels any held attack; the press itself never reaches
    // the document listeners the world reads.
    onClaimInput?: () => void
    // Hand the game's keyboard over (true) or take it back (false).
    suppressGameKeys: (suppressed: boolean) => void
}

const PROMPT_FIELD_CLASS = "samsara-prompt-field"
let styled = false

const ensureStyle = () => {
    if (styled || typeof document === "undefined") return
    styled = true
    const style = document.createElement("style")
    style.textContent = `.${PROMPT_FIELD_CLASS}::placeholder { color: #8f7d62; opacity: 1; }
.${PROMPT_FIELD_CLASS}::-webkit-scrollbar { width: 6px; }
.${PROMPT_FIELD_CLASS}::-webkit-scrollbar-thumb { background: #5a4630; border-radius: 3px; }`
    document.head.appendChild(style)
}

// Everything a press or a key can raise. All of them stop at the field: the world reads presses off
// the document, and the game's hotkeys read keys there too.
const SWALLOWED = ["pointerdown", "pointerup", "mousedown", "mouseup", "touchstart", "touchend", "click", "dblclick",
    "contextmenu", "wheel", "keyup", "keypress", "paste"]

export class PromptField {
    private node?: HTMLTextAreaElement
    private shownAt = ""

    constructor(private props: PromptFieldProps) {}

    private ensure(): HTMLTextAreaElement | undefined {
        if (this.node) return this.node
        if (typeof document === "undefined") return undefined
        const node = document.createElement("textarea")
        node.className = PROMPT_FIELD_CLASS
        node.setAttribute("aria-label", this.props.ariaLabel)
        node.placeholder = this.props.placeholder
        node.maxLength = this.props.maxLength
        node.rows = 2
        node.setAttribute("enterkeyhint", "send")
        node.setAttribute("autocapitalize", "sentences")
        Object.assign(node.style, {
            position: "fixed",
            margin: "0",
            border: "none",
            outline: "none",
            resize: "none",
            background: "transparent",
            color: "#ffedc7",
            caretColor: "#ffd166",
            fontFamily: "Verdana, Geneva, sans-serif",
            lineHeight: "1.3",
            boxSizing: "border-box",
            overflowY: "auto",
            zIndex: "40",
            display: "none",
        } as CSSStyleDeclaration)
        ensureStyle()

        node.addEventListener("input", () => this.props.onInput(node.value))
        node.addEventListener("keydown", event => {
            event.stopPropagation()
            if (event.key === "Escape") {
                event.preventDefault()
                node.blur()
                return
            }
            // isComposing: an IME's own Enter confirms a character, it does not send the message.
            if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
                event.preventDefault()
                this.props.onSubmit()
            }
        })
        for (const name of SWALLOWED) {
            node.addEventListener(name, event => {
                event.stopPropagation()
                if (name === "pointerdown" || name === "mousedown" || name === "touchstart") this.props.onClaimInput?.()
            })
        }
        node.addEventListener("focus", () => {
            this.props.suppressGameKeys(true)
            this.props.onClaimInput?.()
            this.props.onFocusChange?.(true)
        })
        node.addEventListener("blur", event => {
            // Focus moving straight into another text field (the chat composer) keeps the keyboard
            // away from the game: that field has already claimed it, and releasing here would undo it.
            const next = event.relatedTarget as HTMLElement | null
            if (!next || !/^(INPUT|TEXTAREA)$/.test(next.tagName)) this.props.suppressGameKeys(false)
            this.props.onFocusChange?.(false)
        })
        document.body.appendChild(node)
        this.node = node
        return node
    }

    /** Put the field over this page rectangle and show it. Style writes only when it moved. */
    show(rect: OverlayRect, touch = false) {
        const node = this.ensure()
        if (!node) return
        const key = `${Math.round(rect.left)},${Math.round(rect.top)},${Math.round(rect.width)},${Math.round(rect.height)},${touch}`
        if (key === this.shownAt && node.style.display === "block") return
        this.shownAt = key
        // 16px is the floor iOS zooms a focused field up to; a desktop box too short for two lines of
        // that steps down rather than showing one and a half.
        const fontSize = touch ? 16 : Math.max(11, Math.min(16, Math.floor(rect.height / 2.9)))
        const pad = Math.max(3, Math.round(fontSize * 0.4))
        Object.assign(node.style, {
            display: "block",
            left: `${Math.round(rect.left)}px`,
            top: `${Math.round(rect.top)}px`,
            width: `${Math.round(rect.width)}px`,
            height: `${Math.round(rect.height)}px`,
            fontSize: `${fontSize}px`,
            padding: `${pad}px ${pad + 2}px`,
        } as CSSStyleDeclaration)
    }

    hide() {
        if (!this.node) return
        if (this.isFocused()) this.node.blur()
        this.node.style.display = "none"
        this.shownAt = ""
    }

    isShown = (): boolean => this.node?.style.display === "block"
    isFocused = (): boolean => !!this.node && typeof document !== "undefined" && document.activeElement === this.node
    value = (): string => this.node?.value ?? ""

    setValue(value: string) {
        const node = this.ensure()
        if (node && node.value !== value) {
            node.value = value
            this.props.onInput(value)
        }
    }

    focus() {
        this.node?.focus()
    }

    blur() {
        if (this.isFocused()) this.node?.blur()
    }

    /** Off the page for good. Nothing else will remove it: it is not in the pixi tree. */
    dispose() {
        this.hide()
        this.node?.remove()
        this.node = undefined
    }
}
